import { Article } from '@/api/requests/articles/articles';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Box, Stack, Text, PureTextButton } from '@/theme/components';
import { styled } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { useLastArticles } from './useArticles';

type ImageBoxProps = {
  src: string;
};

const ImageBox = styled(Box)<ImageBoxProps>(({ theme, src }) => ({
  backgroundImage: `url('${src}')`,
  backgroundPosition: 'left',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  height: '85px',
  minWidth: '130px',
  margin: 'auto 0',
  [theme.breakpoints.up('lg')]: {
    borderRadius: `${theme.spacing(0.5)} 0 0 ${theme.spacing(0.5)}`,
  },
  [theme.breakpoints.down('md')]: {
    margin: `auto ${theme.spacing(1.25)}`,
    borderRadius: '0',
  },
}));

export const UnderlineButton = styled(PureTextButton)(() => ({
  fontSize: '0.875rem',
  padding: '0',
  width: 'fit-content',
  ':hover': {
    textDecoration: 'underline',
  },
}));

export const Articles = () => {
  const { data } = useLastArticles();
  const { isMobile } = useScreenSize();
  const {
    links: { blogLink },
  } = useAppConstantsContext();

  const handleOpenLinkInNewTab = (link: string) => {
    window.open(link, '_blank');
  };

  return (
    <Stack>
      <Text variant="h3" mb={2.5}>
        Articles for you
      </Text>
      <Stack
        flexDirection={isMobile ? 'column' : 'row'}
        flexWrap="wrap"
        justifyContent="space-between"
        rowGap={1}
        mb={2.5}
      >
        {data?.articles?.slice(0, 4).map((article: Article) => (
          <Stack
            key={article.uuid}
            flexDirection="row"
            width={isMobile ? '100%' : 'calc(50% - 10px)'}
            borderRadius={1}
            sx={(theme) => ({
              boxShadow: `0 0 10px ${theme.palette.primary.main}`,
            })}
          >
            <ImageBox src={article.image} />
            <Stack flexDirection="column" p={1} justifyContent="space-between">
              <Text key={article.uuid} fontSize="1.125rem">
                {article.title}
              </Text>
              <UnderlineButton
                onClick={() => handleOpenLinkInNewTab(article.link)}
              >
                Read more
              </UnderlineButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <UnderlineButton
        onClick={() => handleOpenLinkInNewTab(blogLink)}
        sx={{
          margin: 'auto',
        }}
      >
        See all articles
      </UnderlineButton>
    </Stack>
  );
};
